import { lazy } from 'react';
import { toast } from 'sonner';
import {
  RiskLanding, CommunityLanding, ServicesLanding, DeveloperLanding
} from '@/components/FacetLandingPages';
import ModuleLanding from '@/components/layout/ModuleLanding';
import TopicsLanding from '@/components/group/TopicsLanding';
import TopicRoom from '@/components/group/TopicRoom';
import { ResidentsDirectory } from '@/components/UsSection';
import { topicFromSection } from '@/lib/topics';
import { ClipboardList } from 'lucide-react';
import DeveloperDashboard from '@/components/DeveloperDashboard';
import VerifyOwnership from '@/components/VerifyOwnership';
import { SECTION_REDIRECTS, isReferenceView } from '@/lib/navTree';
import RoleGate from '@/components/layout/RoleGate';

// Lazy-loaded heavy components (code splitting)
const ProjectOverview = lazy(() => import('@/components/ProjectOverview'));
const MySubmissions = lazy(() => import('@/components/MySubmissions'));
const MonitorPrototype = lazy(() => import('@/components/monitoring-prototype/MonitorPrototype'));
const ExpertGuidance = lazy(() => import('@/components/knowledge/ExpertGuidance'));
const BuyerResources = lazy(() => import('@/components/knowledge/BuyerResources'));
const NewsAndMedia = lazy(() => import('@/components/NewsAndMedia'));
const ProfessionalServices = lazy(() => import('@/components/ProfessionalServices'));
const AdminPanel = lazy(() => import('@/components/AdminPanel'));
const UsSection = lazy(() => import('@/components/UsSection'));
const MeSection = lazy(() => import('@/components/MeSection'));
const SubscriptionPage = lazy(() => import('@/components/subscription/SubscriptionPage'));
const DeveloperConsole = lazy(() => import('@/components/DeveloperConsole'));
const ProfessionalDashboard = lazy(() => import('@/components/ProfessionalDashboard'));
const DeveloperShowcase = lazy(() => import('@/components/DeveloperShowcase'));
const S18CollectiveDashboard = lazy(() => import('@/components/S18CollectiveDashboard'));
const MyProjectDashboard = lazy(() => import('@/components/MyProjectDashboard'));
const DeveloperRegistration = lazy(() => import('@/components/developer-showcase/DeveloperRegistration'));
const AdminApprovalQueue = lazy(() => import('@/components/developer-showcase/AdminApprovalQueue'));

const BrowseProjects = lazy(() => import('@/components/prospective/BrowseProjects'));
const EMICalculator = lazy(() => import('@/components/prospective/EMICalculator'));
const BuyersJourney = lazy(() => import('@/components/prospective/BuyersJourney'));

const SiteMap = lazy(() => import('@/pages/SiteMap'));
const AboutFBC = lazy(() => import('@/components/AboutFBC'));

const SiteVisitBooking = lazy(() => import('@/components/prospective/SiteVisitBooking'));
const RequestCallback = lazy(() => import('@/components/prospective/RequestCallback'));
const DownloadBrochure = lazy(() => import('@/components/prospective/DownloadBrochure'));
const ConnectWithBuyers = lazy(() => import('@/components/prospective/ConnectWithBuyers'));

const DueDiligenceRequest = lazy(() => import('@/components/prospective/DueDiligenceRequest'));
const LegalConsultation = lazy(() => import('@/components/prospective/LegalConsultation'));
const LoanFacilitation = lazy(() => import('@/components/prospective/LoanFacilitation'));
const RegistrationGuide = lazy(() => import('@/components/prospective/RegistrationGuide'));

const PropertyMarketplace = lazy(() => import('@/components/owner-services/PropertyMarketplace'));
const ListProperty = lazy(() => import('@/components/owner-services/ListProperty'));
const HomeServicesModule = lazy(() => import('@/components/HomeServicesModule'));

const PrePossessionModule = lazy(() => import('@/components/PrePossessionModule'));
const PostPossessionIssues = lazy(() => import('@/components/PostPossessionIssues'));
const PostPossessionRights = lazy(() => import('@/components/PostPossessionRights'));

const LMSDashboard = lazy(() => import('@/components/lms/LMSDashboard'));
const LMSAdminDashboard = lazy(() => import('@/components/lms/LMSAdminDashboard'));

const LegalServices = lazy(() => import('@/components/LegalServices'));
const SubventionClaims = lazy(() => import('@/components/SubventionClaims'));
const ServiceProviders = lazy(() => import('@/components/ServiceProviders'));

const LawyerDashboard = lazy(() => import('@/components/legal/LawyerDashboard'));
const LawyerRegistration = lazy(() => import('@/components/legal/LawyerRegistration'));

const DistressedBuyerTriage = lazy(() => import('@/components/DistressedBuyerTriage'));

const BuyersChecklist = lazy(() => import('@/components/BuyersChecklist'));
const S18Hub = lazy(() => import('@/components/S18Hub'));
const CorpusWarRoom = lazy(() => import('@/components/CorpusWarRoom'));
const ProjectRequestInvite = lazy(() => import('@/components/ethos/ProjectRequestInvite'));

// Small placeholder for "coming soon" style sections
const Placeholder = ({ title, description }) => (
  <div className="p-8 text-center" data-testid={`placeholder-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <h2 className="text-2xl font-bold mb-4">{title}</h2>
    <p className="text-muted-foreground">{description}</p>
  </div>
);

/**
 * Returns the React element for a given section id.
 * ctx: {
 *   user, isAdmin, isVerifiedBuyer, selectedProjectId,
 *   setActiveSection, setSelectedProjectId,
 *   setFindProjectOpen, openAuthDialog
 * }
 */
export function getSectionContent(rawSection, ctx) {
  const section = SECTION_REDIRECTS[rawSection] || rawSection;
  const {
    user, isAdmin, isVerifiedBuyer, selectedProjectId,
    setActiveSection, setSelectedProjectId,
    setFindProjectOpen, openAuthDialog
  } = ctx;

  // Shared props for most ProjectOverview invocations
  const poProps = {
    onFindProject: () => setFindProjectOpen(true),
    onOpenAuth: openAuthDialog,
    onNavigate: setActiveSection,
    onProjectResolved: setSelectedProjectId
  };
  const projectId = user?.project_id || selectedProjectId || 'ebdab734-e651-45f8-969f-c1bbe525503e';

  const gate = (who) => <RoleGate who={who} user={user} onOpenAuth={openAuthDialog} onNavigate={setActiveSection} />;
  const isDev = user?.role === 'developer';
  const signedIn = (el) => (user ? el : gate(null));

  const topic = topicFromSection(section);
  if (topic) {
    return <TopicRoom key={topic.key} topic={topic} projectId={projectId} onNavigate={setActiveSection}
      onOpenAuth={openAuthDialog} isVerified={isVerifiedBuyer || isAdmin} isAdmin={isAdmin} />;
  }


  switch (section) {
    case 'sample-project':
      return <ProjectOverview isDemo={true} onFindProject={() => setFindProjectOpen(true)} />;

    // ========== MY PROJECT - DIRECT CONTENT SECTIONS ==========
    case 'project-litigation':
      return <ProjectOverview section="litigation" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-dev-info':
      return <ProjectOverview section="dev-info" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-dev-im':
      return <ProjectOverview section="dev-im" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-dev-details':
      return <ProjectOverview section="dev-details" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-dev-tracking':
      return <ProjectOverview section="dev-tracking" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-dev-engagement':
      return <ProjectOverview section="dev-engagement" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-buyer-info':
      return <ProjectOverview section="fbc-info" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-buyer-dd':
      return <ProjectOverview section="fbc-dd" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-buyer-obs':
      return <ProjectOverview section="fbc-obs" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-buyer-voice':
      return <ProjectOverview section="fbc-voice" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-dossier':
      return <ProjectOverview section="dossier" initialProjectId={selectedProjectId} {...poProps} />;
    // Lifecycle-aware landings: post-OC → Issues & Resolution; every project has its Story.
    case 'project-overview':
      return <ProjectOverview section="overview" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-delivery':
      return <ProjectOverview section="delivery" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-money':
      return <ProjectOverview section="money" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-living':
      return <ProjectOverview section="living" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-archive':
      return <ProjectOverview section="archive" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project-story':
      return <ProjectOverview section="story" initialProjectId={selectedProjectId} {...poProps} />;
    case 'project':
      return <ProjectOverview section="auto" initialProjectId={selectedProjectId} {...poProps} />;

    // ========== PROSPECTIVE BUYER MODULE ==========
    case 'browse-projects':
      return <BrowseProjects
        onOpenProject={(p) => {
          setSelectedProjectId(p.id);
          // Land on RERA Litigation by default; fall back to the RERA record when
          // the project has no complaints yet (an empty library is a weak welcome).
          setActiveSection('project-overview');
        }}
        onNavigate={(s, pid) => {
          if (s === 'project-details' && pid) {
            setSelectedProjectId(pid);
            setActiveSection('project-details');
          } else {
            setActiveSection(s);
          }
        }}
      />;
    // RERA Litigation: the default landing when a project has complaints on record.
    // Due-Diligence Index entry point: the RERA public record is always the
    // first disclosure; other layers are reachable via the canonical tab bar.
    case 'project-record':
      return <ProjectOverview
        section="rera"
        initialProjectId={selectedProjectId}
        {...poProps}
      />;
    case 'project-details':
      return <ProjectOverview
        section="fbc-info"
        initialProjectId={selectedProjectId}
        {...poProps}
      />;
    case 'emi-calculator':
      return <EMICalculator />;
    case 'buyers-journey':
      return <BuyersJourney onNavigate={setActiveSection} />;

    case 'pb-site-visit':
      return <SiteVisitBooking />;
    case 'pb-callback':
      return <RequestCallback />;
    case 'pb-brochure':
      return <DownloadBrochure />;
    case 'pb-connect-buyers':
      return <ConnectWithBuyers />;

    case 'pb-dd-request':
      return <DueDiligenceRequest />;
    case 'pb-legal':
      return <LegalConsultation />;
    case 'pb-loan':
      return <LoanFacilitation />;
    case 'pb-registration':
      return <RegistrationGuide />;

    // ========== OTHER SECTIONS ==========
    case 'my-submissions':
      return signedIn(<MySubmissions onOpenProject={(id) => { setSelectedProjectId(id); setActiveSection('project-overview'); }}
        onAddProject={() => window.dispatchEvent(new CustomEvent('fbc-request-project', { detail: {} }))} />);
    case 'project-request':
      return <ProjectRequestInvite onOpenAuth={openAuthDialog} onNavigate={setActiveSection}
        onOpenProject={(id) => { setSelectedProjectId(id); setActiveSection('project-overview'); }} />;
    case 'me':
      return signedIn(<MeSection />);
    case 'subscription':
      return <SubscriptionPage />;
    case 'my-documents':
      return signedIn(<MeSection defaultSection="verification" />);
    case 'us':
      return <UsSection projectId={projectId} />;
    case 'us-directory':
      return <div className="p-4 sm:p-6"><ResidentsDirectory projectId={projectId} /></div>;
    case 'my-project-dashboard':
      return <MyProjectDashboard projectId={projectId} onNavigate={setActiveSection} />;
    case 'us-correspondence':
      return <UsSection projectId={projectId} defaultTab="correspondence" />;
    case 'us-notices':
      return <UsSection projectId={projectId} defaultTab="notices" />;
    case 'us-threads':
      return <UsSection projectId={projectId} defaultTab="threads" />;
    case 'us-meetings':
      return <UsSection projectId={projectId} defaultTab="meetings" />;
    case 'education':
      return <ExpertGuidance />;
    case 'buyer-resources':
      return <BuyerResources onNavigate={setActiveSection} />;
    case 'news':
      return <NewsAndMedia />;
    case 'professional':
      return <ProfessionalServices />;

    // ========== FBC SERVICES ==========
    case 'legal-services':
    case 'us-s18-collective':
      return <S18Hub
        defaultTab={section === 'us-s18-collective' ? 'together' : 'individual'}
        individual={<LegalServices />}
        together={<S18CollectiveDashboard projectName={user?.project_name || 'Ten BKC'} onFileCase={() => setActiveSection('legal-services')} />}
      />;
    case 'subvention-claims':
      return <SubventionClaims />;
    case 'service-providers':
      return <ServiceProviders />;

    // ========== FACET LANDING PAGES ==========
    case 'recourse-landing':
      return (
        <ModuleLanding trunkId="rights" onNavigate={setActiveSection}
          intro={<p className="text-sm rounded-lg border border-amber-200 bg-amber-50/60 dark:bg-amber-900/10 text-amber-900 dark:text-amber-200 p-3" data-testid="rights-intro">
            <strong>FBC's approach:</strong> we help buyers and developers resolve issues constructively. Where that fails, we help you understand and use your legal rights, together with other buyers.
          </p>}
          comingSoon={[{ id: 'quality-issues', icon: ClipboardList, label: 'Quality Issues', hint: 'Report and track deviations from promised specifications' }]} />
      );
    case 'buying-landing':
      return <ModuleLanding trunkId="buying" onNavigate={setActiveSection} />;
    case 'learn-landing':
      return <ModuleLanding trunkId="learn" onNavigate={setActiveSection} />;
    case 'risk-landing':
      return <BuyersChecklist onNavigate={setActiveSection} isVerified={isVerifiedBuyer} />;
    case 'community-landing':
      return <TopicsLanding projectId={projectId} onNavigate={setActiveSection} isVerified={isVerifiedBuyer} isAdmin={isAdmin}
        showcase={isReferenceView(projectId, user)} onFindProject={() => setFindProjectOpen(true)} />;
    case 'services-landing':
      return <ServicesLanding onNavigate={setActiveSection} />;

    // Placeholder / Coming-soon
    case 'quality-issues':
      return <Placeholder title="Quality Issues" description="Coming Soon - Report and track quality deviations" />;
    case 'title-dd':
      return <RiskLanding onNavigate={setActiveSection} isVerified={isVerifiedBuyer} focusPillar="title-assurance" />;
    case 'statutory-dd':
      return <RiskLanding onNavigate={setActiveSection} isVerified={isVerifiedBuyer} focusPillar="approval-assurance" />;
    case 'financial-dd':
      return <RiskLanding onNavigate={setActiveSection} isVerified={isVerifiedBuyer} focusPillar="financial-assurance" />;
    case 'loan-subvention-safety':
      return <RiskLanding onNavigate={setActiveSection} isVerified={isVerifiedBuyer} focusPillar="loan-subvention-safety" />;
    case 'group-buying':
      return <Placeholder title="Group Buying" description="Coming Soon - Collective purchasing deals" />;
    case 'verify-ownership':
      return <VerifyOwnership />;

    // Distressed Buyer Triage
    case 'get-help':
    case 'triage':
      return <DistressedBuyerTriage
        onNavigate={(s, pid) => {
          if (pid) setSelectedProjectId(pid);
          setActiveSection(s);
        }}
        onComplete={(data) => {
          localStorage.setItem('fbc_triage_data', JSON.stringify(data));
        }}
      />;

    // ========== DEVELOPER UPDATES FACET ==========
    case 'developer-landing':
      return <DeveloperLanding onNavigate={setActiveSection} />;
    case 'project-progress':
      return <ProjectOverview section="dev-tracking" initialProjectId={selectedProjectId} {...poProps} />;

    // ========== DEVELOPER DASHBOARD ==========
    case 'dev-my-projects':
      return isDev ? <DeveloperDashboard user={user} defaultTab="projects" /> : gate('developer accounts');
    case 'dev-verifications':
      return isDev ? <DeveloperDashboard user={user} defaultTab="verifications" /> : gate('developer accounts');
    case 'dev-post-updates':
      return isDev ? <DeveloperDashboard user={user} defaultTab="updates" /> : gate('developer accounts');
    case 'dev-issues':
      return isDev ? <DeveloperDashboard user={user} defaultTab="issues" /> : gate('developer accounts');

    // ========== DEVELOPER SHOWCASE ==========
    // Developer Showcase parked (3 Nov 2026): FBC admins only, no public display.
    case 'developer-directory':
    case 'developer-featured':
    case 'developer-leaderboard':
      return isAdmin ? <DeveloperShowcase initialTab={section.replace('developer-', '')} /> : gate('FBC admins');
    case 'developer-register':
      return <DeveloperRegistration
        onSuccess={() => {
          toast.success('Registration submitted! Complete the verification to proceed.');
          setActiveSection('developer-console');
        }}
        onCancel={() => setActiveSection('home')}
      />;
    case 'developer-approval':
      return isAdmin ? <AdminApprovalQueue /> : gate('FBC admins');

    // ========== OWNER SERVICES ==========
    case 'property-marketplace':
      return <PropertyMarketplace onNavigate={setActiveSection} />;
    case 'list-property':
      return <ListProperty onNavigate={setActiveSection} />;
    case 'my-listings':
      return <PropertyMarketplace onNavigate={setActiveSection} showMyListings={true} />;
    case 'home-services':
      return <HomeServicesModule />;

    // ========== POSSESSION MODULES ==========
    case 'pre-possession':
      return <PrePossessionModule projectId="default" projectName="My Project" />;
    case 'post-possession':
      return <PostPossessionIssues />;
    case 'post-possession-rights':
      return <PostPossessionRights />;

    // ========== LMS ==========
    case 'lms-dashboard':
      return signedIn(<LMSDashboard user={user} />);
    case 'lms-admin':
      return isAdmin ? <LMSAdminDashboard /> : gate('FBC admins');

    // ========== ROLE DASHBOARDS ==========
    case 'developer-console':
      return isDev ? <DeveloperConsole /> : gate('developer accounts');
    case 'professional-dashboard':
      return user?.role === 'professional' ? <ProfessionalDashboard /> : gate('professional accounts');
    case 'monitor-prototype':
      return isAdmin ? <MonitorPrototype /> : gate('FBC admins');
    case 'admin':
      return isAdmin ? <AdminPanel /> : gate('FBC admins');

    // ========== LAWYER PORTAL ==========
    case 'lawyer-dashboard':
      return user ? <LawyerDashboard /> : gate('panel lawyers');
    case 'lawyer-registration':
      return <LawyerRegistration />;

    case 'sitemap':
      return <SiteMap onNavigate={setActiveSection} onBack={() => setActiveSection('home')} />;

    case 'about':
      return <AboutFBC onBack={() => setActiveSection('home')} onNavigate={setActiveSection} />;

    case 'corpus-warroom':
      return <CorpusWarRoom projectId={projectId} onNavigate={setActiveSection} />;

    default:
      return null;
  }
}
